import {readAppConfig} from '../../shared/app-config';

export const DEFAULT_CAPACITY_CONSTRAINTS: number[] = [.9, .8, .7, .6, 0];

// 1 at the best end rising toward the worst, so a given colour carries one digit in every metric
const BAND_MARKERS: {[colorClass: string]: string} = {
    'cap-con-low': '1', 'rev-opp-high': '1', 'pacing-high': '1',
    'cap-con-med-low': '2', 'rev-opp-med-high': '2', 'pacing-med-high': '2',
    'cap-con-med': '3', 'rev-opp-med': '3', 'pacing-med': '3',
    'cap-con-med-high': '4', 'rev-opp-med-low': '4', 'pacing-med-low': '4',
    'cap-con-high': '5', 'rev-opp-low': '5', 'pacing-low': '5',
    'pacing-lowest': '6',
    'walk-up-override': 'W'
};

export class SharedChart {
    // Read once per redraw and passed in, never memoized: a cached copy would survive a
    // config change or a TOC switch and paint the chart with the wrong thresholds
    public static getCapacityConstraints(): number[] {
        const configured = readAppConfig()['capacity_constraints'];

        // The API always sends the key but its value is null for a tenant with no
        // capacity_constraints parameter row, so presence is not enough to trust it
        if (!Array.isArray(configured) || configured.length < DEFAULT_CAPACITY_CONSTRAINTS.length) {
            return DEFAULT_CAPACITY_CONSTRAINTS;
        }

        return configured.every(v => typeof v === 'number' && isFinite(v))
            ? configured
            : DEFAULT_CAPACITY_CONSTRAINTS;
    }

    public static getMetricColorClass(value: any, metricId: string, constraints?: number[]): string {
        const capacityConstraints: number[] = constraints || SharedChart.getCapacityConstraints();

        let baseCssClass = '';
        if (value === null || value === undefined) {
            return 'unavailable';
        }

        if (metricId === 'capacityConstraint') {
            baseCssClass = 'cap-con';
            switch (true) {
                case (value > capacityConstraints[0]):
                    return `${baseCssClass}-high`;
                case (value > capacityConstraints[1]):
                    return `${baseCssClass}-med-high`;
                case (value > capacityConstraints[2]):
                    return `${baseCssClass}-med`;
                case (value > capacityConstraints[3]):
                    return `${baseCssClass}-med-low`;
                case (value >= capacityConstraints[4]):
                    return `${baseCssClass}-low`;
                default:
                    return 'unavailable';
            }
        } else if (metricId === 'pacing') {
            baseCssClass = 'pacing';
            switch (true) {
                case (value > 20):
                    return `${baseCssClass}-high`;
                case (value > 10):
                    return `${baseCssClass}-med-high`;
                case (value > 0):
                    return `${baseCssClass}-med`;
                case (value > -10):
                    return `${baseCssClass}-med-low`;
                case (value > -20):
                    return `${baseCssClass}-low`;
                case (value < -20):
                    return `${baseCssClass}-lowest`;
                default:
                    return 'unavailable';
            }
        } else if (metricId === 'revenueOpportunity') {
            baseCssClass = 'rev-opp';
            switch (true) {
                case (value > 1500):
                    return `${baseCssClass}-high`;
                case (value > 750):
                    return `${baseCssClass}-med-high`;
                case (value > 250):
                    return `${baseCssClass}-med`;
                case (value >= 0):
                    return `${baseCssClass}-med-low`;
                case (value < 0):
                    return `${baseCssClass}-low`;
                default:
                    return 'unavailable';
            }
        } else if (metricId === 'walkUpOverride') {
            return value ? 'walk-up-override' : 'unavailable';
        }
    }

    public static getBandMarker(colorClass: string): string {
        return BAND_MARKERS[colorClass] || '';
    }

    public static formatMetricValue(value: any, metricId: string): string {
        if (value === null || value === undefined) {
            return 'Unavailable';
        }
        switch (metricId) {
            case 'capacityConstraint':
                return `${Math.round(value * 100)}%`;
            case 'walkUpOverride':
                return value ? 'Yes' : 'No';
            case 'pacing':
                return Number(value).toFixed(1);
            default:
                return `${Math.round(value)}`;
        }
    }
}

export class SharedChartMenuEvent {
    action: SharedChartMenuAction;
    data: any;
}

export enum SharedChartMenuAction {
    viewDetails,
    viewTubeChart, // currently not using this enum, using viewDetails instead
    viewODs,
    overrideAdvanced,
    overrideWalkUp,
    toggleAutoPilot
}
